import React from 'react';
import { Link } from 'react-router-dom';
import { WizardProvider, useWizard } from '../../context/WizardContext';
import { WizardProgressBar } from '../../components/wizard/WizardProgressBar';
import { Step0Class } from './steps/Step0Class';
import { Step0Version } from './steps/Step0Version';
import { Step1Department } from './steps/Step1Department';
import { Step1Subject } from './steps/Step1Subject';
import { Step2Paper } from './steps/Step2Paper';
import { Step3Chapter } from './steps/Step3Chapter';
import { Step4ExamType } from './steps/Step4ExamType';
import { Step5Questions } from './steps/Step5Questions';
import { Step7Review } from './steps/Step7Review';
import { Step8Generate } from './steps/Step8Generate';
import type { WizardStep } from '../../types/wizard';
import type { GeneratedExamPaper } from '../../types/generated';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { LanguageToggle } from '../../components/core/LanguageToggle';

const STEP_COMPONENTS: Record<WizardStep, React.ComponentType> = {
  class: Step0Class,
  version: Step0Version,
  department: Step1Department,
  subject: Step1Subject,
  paper: Step2Paper,
  chapter: Step3Chapter,
  examType: Step4ExamType,
  questions: Step5Questions,
  review: Step7Review,
  generate: Step8Generate,
};

// ─── Full-page wizard (used at /wizard route) ─────────────────────────────────

function WizardContent() {
  const { currentStep } = useWizard();
  const { t } = useTranslation();
  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Ambient blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-violet-500/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-indigo-500/5 blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-slate-700 bg-slate-800/90 backdrop-blur-xl">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 py-3.5 flex items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-violet-400 transition-colors mr-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('common.dashboard')}
          </Link>

          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 shadow-md shadow-brand-500/25">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-white leading-none tracking-tight">
              {t('common.appName')}
            </h1>
            <p className="text-[11px] text-slate-500 mt-0.5">{t('common.appTagline')}</p>
          </div>
          <div className="flex-1" />
          <LanguageToggle />
        </div>
      </header>

      {/* Main */}
      <main className="relative mx-auto max-w-2xl px-4 sm:px-6 py-6 space-y-4">
        {/* Progress card */}
        <div className="rounded-2xl bg-slate-800 border border-slate-700 shadow-sm p-5">
          <WizardProgressBar />
        </div>

        {/* Step content */}
        <div className="rounded-2xl bg-slate-800 border border-slate-700 shadow-sm p-6 sm:p-8 animate-slide-up">
          <StepComponent />
        </div>
      </main>
    </div>
  );
}

export function WizardPage() {
  return (
    <WizardProvider>
      <WizardContent />
    </WizardProvider>
  );
}

// ─── Inline / embedded wizard (used inside the dashboard overview) ────────────

function EmbeddedWizardContent({ onClose }: Readonly<{ onClose: () => void }>) {
  const { currentStep } = useWizard();
  const { t } = useTranslation();
  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <div className="space-y-4">
      {/* Sub-header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-violet-400 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t('wizard.backToOverview')}
        </button>
        <div className="flex-1" />
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 shadow-md shadow-brand-500/25">
          <Sparkles className="h-3.5 w-3.5 text-white" />
        </div>
        <div>
          <p className="text-xs font-bold text-white leading-none">{t('common.appName')}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{t('common.appTagline')}</p>
        </div>
      </div>

      {/* Progress card */}
      <div className="rounded-2xl bg-slate-800 border border-slate-700 shadow-sm p-5">
        <WizardProgressBar />
      </div>

      {/* Step content */}
      <div className="rounded-2xl bg-slate-800 border border-slate-700 shadow-sm p-6 sm:p-8 animate-slide-up">
        <StepComponent />
      </div>
    </div>
  );
}

export function WizardEmbed({
  onClose,
  onGenerated,
}: Readonly<{ onClose: () => void; onGenerated?: (paper: GeneratedExamPaper) => void }>) {
  return (
    <WizardProvider onComplete={onGenerated}>
      <EmbeddedWizardContent onClose={onClose} />
    </WizardProvider>
  );
}
